import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { BranchEventType, ReferralMethodType } from '../../model/types/branch-event-type';
import { IOrder } from '../../model/models/order/order-interface';
import { ICustomer } from '../../model/models/customer';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { DeeplinkProvider } from '../marketing/deeplink';
import { SegmentProvider } from './segment';
import { APP_CONFIG, Env, Region } from '../../environment';
import { OrderProvider } from '../../providers/order/order';
import { RouteProvider } from '../../providers/route/route';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { setAnonymousId, setCampaignId, setDeeplinkSource, setDeviceId } from './deeplink-source';
import { Subject } from 'rxjs';

@Injectable()
export class BranchProvider {
  branchSdk;
  initPromise;
  initResolve;
  initResolved = false;
  initProfiler;
  directBankPaymentStatusProcessed;
  isFromDeeplink = false;
  resumeSubscribe = false;
  private handleBranchInFlight: Promise<void> = null;
  private lastProcessedClickKey: string = null;
  private cachedAuthProvider = null;
  private cachedOnfidoProvider = null;
  lastIdentity; // OM-19023: last developer_identity pushed to Branch, for dedupe

  // RMT5 PSP redirect result emitted from Branch deeplinks. AppComponent
  // subscribes and forwards to the Vue iframe — keeps VueProvider out of
  // BranchProvider's DI graph (PermissionProvider → BranchProvider would
  // otherwise create a cycle through VueProvider).
  pspRedirectResult$ = new Subject<string>();

  constructor(private platform: Platform,
    private analytics: SegmentProvider,
    private deeplinkProvider: DeeplinkProvider,
    private orderProvider: OrderProvider,
    private routeProvider: RouteProvider,
    private utilsProvider: UtilsProvider,
  ) {
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    })
    // OM-19023: register with SegmentProvider so it can push the Ria customer ID to
    // Branch.setIdentity() from the same chokepoint that identifies the user to Amplitude,
    // keeping Branch developer_identity == Amplitude user_id. Late-bound to avoid a circular
    // dependency (this provider already injects SegmentProvider).
    this.analytics.setBranchProvider(this);
  }

  async initSdk(authProvider, onfidoProvider) {
    // to be on the safe side initialize also a timer to set it to ready otherwise if branch SDK is hanging for whatever reason we would risk to block it (i.e. in Signin this.onLogin = await this.deeplinkProvider.getOnLogin();)
    let timer = window.setTimeout(() => {
      timer = null;
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Timeout for branch SDK ! Proceeding without Branch so the flow is not blocked');
      this.deeplinkProvider.setReady();
      this.resolveInit('timeout');
    }, 10000);

    try {
      this.initProfiler = LoggerProvider.startProfiler();
      LoggerProvider.info(this, `Init branch SDK (platform=${this.platform.is('cordova') ? 'cordova' : 'browser'})`);
      await this.handleBranch(authProvider, onfidoProvider);
      if (!this.resumeSubscribe) {
        this.platform.resume.subscribe(() => {
          this.handleBranch(authProvider, onfidoProvider);
        });
        this.resumeSubscribe = true;
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to init branch SDK');
    } finally {
      LoggerProvider.info(this, 'Finished branch SDK init');
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      this.deeplinkProvider.setReady();
      this.resolveInit('init-complete'); // resolves on SDK-ready; initSuccessCB deeplink work is intentionally not awaited so consumers aren't blocked by it
    }
  }

  // OM-18748: idempotent resolve of initPromise; logs the reason so prod can tell why init unblocked the flow.
  private resolveInit(reason: string) {
    if (this.initResolved)
      return;
    this.initResolved = true;
    const message = `Branch initPromise resolved (reason=${reason})`;
    if (this.initProfiler)
      LoggerProvider.profileLogEntry(this.initProfiler, this, message);
    else
      LoggerProvider.info(this, message);
    this.initResolve();
  }

  private warnSdkUnavailable(context: string) {
    if (this.platform.is('cordova'))
      LoggerProvider.warning(this, LoggerEvent.ProviderException, `Skipping Branch ${context} — SDK unavailable after init`);
  }

  setDMAParamsForEEA(adsConsent: boolean = true){
    if (!this.branchSdk) {
      this.warnSdkUnavailable('setDMAParamsForEEA');
      return;
    }
    const eeaRegion = adsConsent;
    const adPersonalizationConsent = adsConsent;
    const adUserDataUsageConsent = adsConsent;
    this.branchSdk.setDMAParamsForEEA(eeaRegion,adPersonalizationConsent,adUserDataUsageConsent);
    LoggerProvider.info(this, 'Branch Consent Submitted', null);
  }

  async checkBranchDeeplinkInForeground(){
    this.handleBranch(null, null);
  }

  private fakePush(authProvider, onfidoProvider) {
    const data = {
      "~referring_link": "https://riamoneytransfer.test-app.link/mwKbvt7EW1b",
      "~creation_source": 0,
      "$deeplink_path": "validate?t=AA48907ACF0EF5C7A29FF24BDDD41975",
      "+is_first_session": false,
      "$desktop_url": "",
      "MarketingEmailEnabled": false,
      "$android_url": "",
      "$uri_redirect_mode": 0,
      "MarketingSMSEnabled": false,
      "referralMethod": 1,
      "referralProgram": "ARP",
      "Username": "713729042",
      "referralSource": "FxOnline",
      "referralAgentLocationId": "0",
      "CountryDeeplinkEventType": 1,
      "RetVal": 20,
      "Token": "AA48907ACF0EF5C7A29FF24BDDD41975",
      "CountryCode": "34",
      "MarketingWhatsAppEnabled": false,
      "referralAgentType": "Unknown",
      "~id": 1567354295257674200,
      "+click_timestamp": 1774925914,
      "$ios_url": "",
      "UserCountry": "ES",
      "+clicked_branch_link": true,
      "referralAgentId": "35055011",
      "referralMedium": "Signature Pad",
      "+match_guaranteed": true
    }
    this.initSuccessCB(data, authProvider, onfidoProvider);
  }

  private getDeeplinkClickKey(data): string {
    try {
      const clickTimestamp = data && data['+click_timestamp'] !== undefined && data['+click_timestamp'] !== null
        ? String(data['+click_timestamp'])
        : null;
      if (!clickTimestamp) {
        return null;
      }
      const linkId = data['~id'] !== undefined && data['~id'] !== null ? String(data['~id']) : '';
      return `${linkId}:${clickTimestamp}`;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to build Branch deeplink click key');
      return null;
    }
  }

  private async handleBranch(authProvider, onfidoProvider) {
    const resolvedAuthProvider = authProvider || this.cachedAuthProvider;
    const resolvedOnfidoProvider = onfidoProvider || this.cachedOnfidoProvider;
    if (resolvedAuthProvider) {
      this.cachedAuthProvider = resolvedAuthProvider;
    }
    if (resolvedOnfidoProvider) {
      this.cachedOnfidoProvider = resolvedOnfidoProvider;
    }

    if (this.handleBranchInFlight) {
      LoggerProvider.info(this, 'handleBranch already in flight, coalescing duplicate trigger');
      return this.handleBranchInFlight;
    }
    this.handleBranchInFlight = this.runHandleBranch(resolvedAuthProvider, resolvedOnfidoProvider);
    try {
      await this.handleBranchInFlight;
    } finally {
      this.handleBranchInFlight = null;
    }
  }

  private async runHandleBranch(authProvider, onfidoProvider) {
    if (!this.platform.is('cordova') && authProvider && authProvider.isLoggedIn() && APP_CONFIG.region.region === Region.US) {// this fake call should be only for us as it has plaid and page in data cause redirect in wallet to activity page
      this.fakePush(authProvider, onfidoProvider);
      return;
    }
    if (!this.platform.is('cordova')) {
      this.resolveInit('browser');
    
    // if (!this.platform.is('cordova') && authProvider && authProvider.isLoggedIn() && APP_CONFIG.region.region === Region.US) {// this fake call should be only for us as it has plaid and page in data cause redirect in wallet to activity page
      // this.fakePush(authProvider, onfidoProvider);
      // return;
    // }
    
    if (!this.platform.is('cordova'))
      return;
    }

    this.branchSdk = window['Branch'];
    try {
      if (APP_CONFIG.env !== Env.PROD) {
        this.branchSdk.setLogging(true);
      }

      // According to the documentation (https://help.branch.io/partners-portal/docs/segment-export):
      // Here's a sample snippet showing this. NOTE you must set the correct key before calling sessionBuilder()...init(). You must also initialize the Segment SDK before setting the request metadata in the Branch SDK.
      await this.setRequestMetadata(); // currently to be on the safe side we call it before and after but later we can probably remove the second call

      // OM-19355: configure Branch SDK wait time for third-party APIs to >= 5s so Google Ads
      // ICM (Install Conversion Measurement) can attribute iOS installs probabilistically.
      // iOS-only inside the plugin; resolves to a no-op on other platforms. Must be set before initSession().
      try {
        // await this.branchSdk.setSDKWaitTimeForThirdPartyAPIs(5);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to set SDK wait time for third-party APIs');
      }

      const data = await this.branchSdk.initSession();
      if (data && data["deviceId"]) {
        LoggerProvider.info(this, 'Branch link uuid: Before setting to device id', LoggerProvider.uuid);
        LoggerProvider.info(this, 'Branch link: Device id received from branchSDK', data["deviceId"]);
        // Overwrite the deviceId with the one passed by the public site ONLY
        // when the user comes from the public site. This is intended to keep
        // track of the same user across multiple devices correctly.
        LoggerProvider.uuid = data["deviceId"];
        LoggerProvider.info(this, 'Branch link uuid: After setting to device id', LoggerProvider.uuid);
        setDeviceId(data['deviceId']); // set deviceId for tracking in analytics
        LoggerProvider.info(this, 'Captured device ID from branch data', data['deviceId']);
      }
      this.initSuccessCB(data, authProvider, onfidoProvider);
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to init Branch — proceeding without Branch so the flow is not blocked');
      this.resolveInit('init-failed');
    }
  }
/**
 *
 * @param data {
    "+match_guaranteed": true,
    "RetVal": 30,
    "$deeplink_path": "validate?t=Ck9hfIjGnNWZNFC4Hpp1NIQ1p3vr7nYRi5R8YImesphIZ0OgpblPAgqkzt9SclO_cdxayEI3kagSzdqFqaKQgA",
    "~creation_source": 0,
    "Username": "123123213",
    "+clicked_branch_link": true,
    "+click_timestamp": 1625571383,
    "$one_time_use": false,
    "~id": 939415497577515000,
    "+is_first_session": false,
    "Token": "Ck9hfIjGnNWZNFC4Hpp1NIQ1p3vr7nYRi5R8YImesphIZ0OgpblPAgqkzt9SclO_cdxayEI3kagSzdqFqaKQgA",
    "~referring_link": "https://p42uc.test-app.link/0eBdYNcHyhb",
    "CountryCode": "60"
  }
 */
  private async initSuccessCB(data, authProvider, onfidoProvider) {
    LoggerProvider.info(this, 'Check branch data', data);
    try {
      // Capture source parameter for Google Lite Onebox tracking (OM-17521)
      if (data['source']) {
        setDeeplinkSource(data['source']);
        LoggerProvider.info(this, 'Captured deeplink source for tracking', { source: data['source'] });

        // Track Application Opened event with source for Google Lite Onebox attribution
        this.analytics.trackEvent({
          eventName: AnalyticsEventType.ApplicationOpened,
          analyticsData: {
            source: data['source']
          }
        });
      }

      if (data['~campaign'] || data['utm_campaign']) {
        const campaignId = data['~campaign'] || data['utm_campaign'];
        setCampaignId(campaignId);
        LoggerProvider.info(this, 'Captured campaign from branch data', { campaign: campaignId });
      }

      if (data['deviceId']) {
        setDeviceId(data['deviceId']); // set deviceId for tracking in analytics
        LoggerProvider.info(this, 'Captured device ID from branch data', data['deviceId']);
      }
      // Custom Event for Branch lifecyclee as nativee install/click eventss are not reliably exported to Segment
      const branchLifecycleData = {
        referringLink: data['~referring_link'] || null,
        source: data['source'] || null,
        firstSession: !!data['+is_first_session'],
        clickedBranchLink: !!data['+clicked_branch_link']
      };
      if (data['+is_first_session'])
        this.publishCustomEvent(BranchEventType.Install, branchLifecycleData);
      const isBranchLinkClick = !!data['+clicked_branch_link'] ||
        (!!data['+non_branch_link'] && data['+non_branch_link'].indexOf('_branch_referrer') > -1);
      if (isBranchLinkClick)
        this.publishCustomEvent(BranchEventType.Click, branchLifecycleData);

      if (data['+clicked_branch_link']) {
        const clickKey = this.getDeeplinkClickKey(data);
        if (clickKey && this.lastProcessedClickKey === clickKey) {
          LoggerProvider.info(this, 'Skipping duplicate Branch deeplink processing', { clickKey });
          return;
        }
        if (clickKey) {
          this.lastProcessedClickKey = clickKey;
        }
        this.isFromDeeplink = true;
        // read deep link data on click
        LoggerProvider.info(this, 'Received deep link data', data);
        const referralMethod = data['referralMethod'] || data['ReferralMethod'];
        const isAgentReferral = [
          ReferralMethodType.AgentQRCodeExistingCustomer,
          ReferralMethodType.AgentQRCodeNewCustomer,
          ReferralMethodType.ReferralLinkExistingCustomer,
          ReferralMethodType.ReferralLinkNewCustomer
        ].includes(referralMethod)
        // If no page is specified but calculator params are present (e.g. from Google OneBox Lite),
        // Branch SDK may not pass URL query params as top-level keys, so parse them from ~referring_link
        if (!data['page'] && !data['amount'] && data['~referring_link']) {
          try {
            const queryString = data['~referring_link'].split('?')[1];
            if (queryString) {
              const refParams = this.parseQueryParams(queryString);
              Object.keys(refParams).forEach(key => {
                if (data[key] === undefined) data[key] = refParams[key];
              });
            }
          } catch (e) {
            LoggerProvider.info(this, 'Failed to parse ~referring_link query params');
          }
        }
        const hasGoogleLiteSource = data['source'] === 'google-affiliated' ||
          data['googleLiteDeeplink'] ||
          (data['~referring_link'] && data['~referring_link'].indexOf('source=google-affiliated') > -1);
        const isGoogleLiteCalculatorDeeplink = hasGoogleLiteSource &&
          (data['page'] === 'CheckPricesPage' || (data['amount'] && data['countryTo']));

        // Handle old Singpass deep link redirects before the registration flow.This is the only case that requires pre-registration handling to prevent users from seeing the login screen before being redirected to the Singpass screen
        if (data['Action'] === "kyc-singpass" && data['$deeplink_path']) {
          this.deeplinkProvider.processToSingpassDeeplink(data, null, true);
        } else if (data['+non_branch_link'] && data['+non_branch_link'].indexOf('riamoneytransfer') > -1 && data['+non_branch_link'].indexOf('singpass') > -1) {
          LoggerProvider.info(this, 'Process singpass deeplink', data);
          this.deeplinkProvider.processToSingpassDeeplink(data, null);
        }

        if ((data['Username'] || isAgentReferral) && !isGoogleLiteCalculatorDeeplink) {
          LoggerProvider.info(this, 'Process deeplink registration', data);
          if(data.UserCountry)
            await authProvider.setUserCountry(data.UserCountry, true, true, true, false, false);
          this.deeplinkProvider.setDeeplinkDataVueBridge(); // set this for brige to be able to know that the registration is from deeplink and then set route to activity or wallet home accordingly
          this.deeplinkProvider.processDeepLinkRegistration(data)
        }
        if (!data['page'] && data['amount'] && data['countryTo']) {
          data['page'] = 'CheckPricesPage';
          data['googleLiteDeeplink'] = true;
          LoggerProvider.info(this, 'Google Lite calculator deeplink detected, routing to CheckPricesPage');
        }
        if (data['page']) {
          LoggerProvider.info(this, 'Process deeplink params', data);
          this.deeplinkProvider.deeplinkParamsHandeler(data)
        }
        if (data['isPendingOrder'])
          this.deeplinkProvider.setDeeplinkPendingOrder(data['isPendingOrder']);
        if (this.platform.is('ios') && data['paymentMethod'] === 'ideal') {
          LoggerProvider.info(this, 'Check transaction status for ideal', data);
          await this.orderProvider.checkTxnStatus();
        }
        if (data['PromoCode']) {
          LoggerProvider.info(this, 'Process deeplink referral', data);
          await authProvider.setUserCountry(data.CountryCode, true, true, true, false, false);
          this.deeplinkProvider.processReferralDeepLinkRegistration(data);
        }
        if (data['getRiaCard']){
          LoggerProvider.info(this, 'Process deeplink RIA card notifications', data);
          this.deeplinkProvider.setDeeplinkGetRiaCardNotifications(data['getRiaCard']);
        }
        if(data['~advertising_partner_name'] === "Impact" && data['~click_id']) { // Impact radius click_id
          LoggerProvider.info(this, 'Process impact radius', data);
          this.sendImpactRadiusEvent(data['~click_id']);
          this.utilsProvider.setDeeplinkImpactRadius();
        }

        if (data['~referring_link'] && data['~referring_link'].indexOf('https://riamoneytransfer.') > -1 && data['~referring_link'].indexOf('signicat') > -1) {
          LoggerProvider.info(this, 'Process Signicat referring deeplink', data);
          onfidoProvider.processSignicatDeeplink(data, true);
        }
      }
      else if (data['+non_branch_link'] && data['+non_branch_link'].indexOf('SIGNICAT') > -1) {
        LoggerProvider.info(this, 'Process Signicat deeplink', data);
        onfidoProvider.processSignicatDeeplink(data);
      }
      else if (data['+non_branch_link'] && 
        data['+non_branch_link'].indexOf('signicat') > -1 && 
        data['+non_branch_link'].indexOf('mitid') > -1) { // prod signicat deeplink for mitid
        onfidoProvider.processSignicatDeeplink(data, true);
      }
      else if (data['+non_branch_link'] && 
        data['+non_branch_link'].indexOf('riamoneytransfer') > -1 && 
        data['+non_branch_link'].indexOf('/#/result-psp?') > -1 && 
        window.location && window.location.href && window.location.href.indexOf('/#/result-psp?') < 0) { // To make sure we don't process twice the deeplink on redirect
        LoggerProvider.info(this, 'Process adyen deeplink', data);
        this.deeplinkProvider.handlePspRedirectLink(data['+non_branch_link']);
      }

      // RMT5 (Vue) PSP redirect — forward redirectResult to AdyenProcessingPage in the Vue iframe.
      // Handled directly here to keep VueProvider out of DeeplinkProvider's
      // dependency graph (VueProvider already depends on DeeplinkProvider).
      // Mirrors the legacy `/#/result-psp?` matcher above: matches the hash
      // form only so partial URL matches don't trigger this branch.
      else if (data['+non_branch_link'] &&
        data['+non_branch_link'].indexOf('/#/wallet/payment/processing?') > -1 &&
        window.location && window.location.href &&
        window.location.href.indexOf('/#/wallet/payment/processing?') < 0) {
        LoggerProvider.info(this, 'Process RMT5 adyen deeplink', data);
        const redirectResultMatch = data['+non_branch_link'].match(/[?&]redirectResult=([^&]+)/);
        if (redirectResultMatch) {
          const redirectResult = decodeURIComponent(redirectResultMatch[1]);
          this.pspRedirectResult$.next(redirectResult);
        } else {
          LoggerProvider.error(this, LoggerEvent.PageException, 'Missing redirectResult in RMT5 PSP deeplink: ' + data['+non_branch_link']);
        }
      }

      else if (data['+non_branch_link'] && data['+non_branch_link'].indexOf('riamoneytransfer') > -1 && data['+non_branch_link'].indexOf('singpass') > -1) {
        LoggerProvider.info(this, 'Process singpass deeplink', data);
        this.deeplinkProvider.processToSingpassDeeplink(data, null);
      }

      // OM-18681: When Branch returns +clicked_branch_link=false with a +non_branch_link containing
      // _branch_referrer (gzip+base64 encoded), the SDK failed to decode the link params.
      // Use getLatestReferringParams() which has the decoded data, or decompress the referrer as fallback.
      else if (!data['+clicked_branch_link'] && data['+non_branch_link'] && data['+non_branch_link'].indexOf('_branch_referrer') > -1) {
        await this.handleBranchReferrerDeeplink(data);
      }
      // TrueLayer bank-redirect arriving via riamoneytransfer://truelayer-redirect?payment_id=…
      else if (data['+non_branch_link'] && data['+non_branch_link'].indexOf('truelayer-redirect') > -1) {
        const qs = data['+non_branch_link'].split('?')[1];
        const paymentId = qs ? (this.parseQueryParams(qs)['payment_id'] || null) : null;
        LoggerProvider.info(this, 'Received TrueLayer redirect', { paymentId } as any);
      }

      if (!this.directBankPaymentStatusProcessed) {
        if (data['+non_branch_link'] && data['+non_branch_link'].indexOf('riamoneytransfer') > -1) {
          if (data['+non_branch_link'].indexOf('deeplink/ideal') > -1 || data['+non_branch_link'].indexOf('deeplink/giropay') > -1 ||
              data['+non_branch_link'].indexOf('deeplink/bancontact') > -1) {
              LoggerProvider.info(this, 'Check transaction status for direct bank', data);
              await this.orderProvider.checkTxnStatus();
              this.directBankPaymentStatusProcessed = true;
            }
        }
      }
      await this.setRequestMetadata(); // TODO probably can be removed
    }
    catch (exjson) {
      this.isFromDeeplink = false;
      LoggerProvider.exception(this, exjson, LoggerEvent.ProviderException, 'Failed to parse Branch init result');
    }
    LoggerProvider.info(this, 'Branch initialized successfully', data);
    this.resolveInit('success');
  }

  private async handleBranchReferrerDeeplink(data) {
    try {
      const latestParams = await this.branchSdk.getLatestReferringParams();
      if (latestParams && latestParams['+clicked_branch_link']) {
        if (!latestParams['page'] && latestParams['amount'] && latestParams['countryTo']) {
          latestParams['page'] = 'CheckPricesPage';
          latestParams['googleLiteDeeplink'] = true;
        }
        if (latestParams['page']) {
          this.isFromDeeplink = true;
          LoggerProvider.info(this, 'Google Lite deeplink resolved via getLatestReferringParams', latestParams['page']);
          this.deeplinkProvider.deeplinkParamsHandeler(latestParams);
        }
      } else {
        // Fallback: decompress _branch_referrer manually (gzip+base64)
        const nonBranchLink = data['+non_branch_link'];
        const referrerMatch = nonBranchLink.match(/[?&]_branch_referrer=([^&]+)/);
        if (referrerMatch && typeof (window as any).DecompressionStream === 'function') {
          const decodedUrl = await this.decompressGzipBase64(decodeURIComponent(referrerMatch[1]));
          const qsPart = decodedUrl.split('?')[1];
          if (qsPart) {
            const calcParams = this.parseQueryParams(qsPart);
            if (calcParams['amount'] && calcParams['countryTo']) {
              Object.assign(data, calcParams);
              data['page'] = 'CheckPricesPage';
              data['googleLiteDeeplink'] = true;
              this.isFromDeeplink = true;
              LoggerProvider.info(this, 'Google Lite deeplink resolved via _branch_referrer decompression');
              this.deeplinkProvider.deeplinkParamsHandeler(data);
            }
          }
        }
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to process _branch_referrer deeplink');
    }
  }

  private async decompressGzipBase64(encoded: string): Promise<string> {
    const binaryStr = atob(encoded);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    const ds = new (window as any).DecompressionStream('gzip');
    const writer = ds.writable.getWriter();
    writer.write(bytes);
    writer.close();
    const reader = ds.readable.getReader();
    const chunks = [];
    let readDone = false;
    while (!readDone) {
      const result = await reader.read();
      if (result.value) chunks.push(result.value);
      readDone = result.done;
    }
    const totalLen = chunks.reduce((acc, c) => acc + c.length, 0);
    const combined = new Uint8Array(totalLen);
    let byteOffset = 0;
    for (const chunk of chunks) {
      combined.set(chunk, byteOffset);
      byteOffset += chunk.length;
    }
    return new (window as any).TextDecoder().decode(combined);
  }

  private parseQueryParams(queryString: string): any {
    const params: any = {};
    queryString.split('&').forEach(param => {
      const eqIndex = param.indexOf('=');
      if (eqIndex < 1) return;
      const key = param.substring(0, eqIndex);
      const value = param.substring(eqIndex + 1);
      if (value) params[decodeURIComponent(key)] = decodeURIComponent(value);
    });
    return params;
  }

  private async setRequestMetadata() {
    try {
      const anonymousId = await this.getSegmentAnonymousId();
      setAnonymousId(anonymousId); // set anonymousId for tracking in analytics
      LoggerProvider.info(this, 'Captured anonymous ID from branch data', anonymousId);
      this.branchSdk.setRequestMetadata('$segment_anonymous_id', anonymousId);
      LoggerProvider.info(this, `Set branch anonymous ID ${anonymousId}`);
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'set request metadata failed')
    }
  }

  private async getSegmentAnonymousId() {
    try {
      return await this.analytics.getAnonymousId()
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get segment anonymousId');
    }
  }

  private async sendImpactRadiusEvent(click_id: String) {
    this.getSegmentAnonymousId().then((anonymous_id) => {
      const analyticsData = {
        clickId: click_id,
        anonymousId: anonymous_id,
        affiliate: 'Impact'
      };

      setAnonymousId(anonymous_id); // set anonymousId for tracking in analytics
      LoggerProvider.info(this, 'Captured anonymous ID from branch data', anonymous_id);

      LoggerProvider.info(this, 'Track Impact radius click id: ' + AnalyticsEventType.AffiliateStarted, analyticsData);

      this.analytics.trackEvent(
        {
          eventName: AnalyticsEventType.ImpactUserStarted,
          analyticsData,
        }
      );
      this.analytics.trackEvent(
        {
          eventName: AnalyticsEventType.AffiliateStarted,
          analyticsData,
        }
      );

      this.publishCustomEvent(BranchEventType.AffiliateStarted, analyticsData);
    });
  }

  public resetDirectBankPaymentStatusProcessed(){
    this.directBankPaymentStatusProcessed = false;
  }

  trackRegistration(newUser: any) {
    if(newUser)
      newUser.customerId = newUser.id;
    this.publishCustomEvent(BranchEventType.NewRegistration, { newUser: newUser });
  }

  trackLoginCreation(newUser: any) {
    this.publishCustomEvent(BranchEventType.CompleteLoginCreation, { newUser: newUser });
  }

  trackOrderCreation(order: IOrder) {
    if (order !== undefined) {
      if (order.customerId === undefined)
      order.customerId = 0;
      if (order.orderNumber === undefined)
      order.orderNumber = '';
      if (order.sendAmountUSD === undefined)
        order.sendAmountUSD = 0;

    }

    const { orderNumber, sendAmountUSD, customerId } = order;

    this.publishCustomEvent(BranchEventType.NewOrder, {
      order: order,
      OrderId: orderNumber,
      revenue: sendAmountUSD,
      ...(customerId ? { customerId: customerId } : {})
    });
  }

  trackFirstOrder(order: IOrder) {
    if (order !== undefined) {
      if (order.customerId === undefined)
      order.customerId = 0;
      if (order.orderNumber === undefined)
      order.orderNumber = '';
      if (order.sendAmountUSD === undefined)
        order.sendAmountUSD = 0;

    }
    const { orderNumber, sendAmountUSD, customerId } = order;

    this.publishCustomEvent(BranchEventType.FirstMobileOrder, {
      order: order,
      OrderId: orderNumber,
      revenue: sendAmountUSD,
      ...(customerId ? { customerId: customerId } : {})
    });
  }

  async setIdentity(identity) {
    try {
      if (identity === undefined || identity === null || `${identity}` === '')
        return;
      const identityStr = `${identity}`;
      // Dedupe: identify funnels through here on every login/registration/settings change, but the
      // developer_identity only needs to be (re)asserted when it actually changes (login, account switch).
      if (this.lastIdentity === identityStr)
        return;
      await this.initPromise;
      if (!this.branchSdk) {
        this.warnSdkUnavailable('setIdentity');
        return;
      }
      await this.branchSdk.setIdentity(identityStr);
      this.lastIdentity = identityStr;
      LoggerProvider.info(this, `Branch setIdentity ${identityStr}`);
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Set Identity failed');
    }
  }

  async logout() {
    try {
      await this.initPromise;
      if (!this.branchSdk) {
        this.warnSdkUnavailable('logout');
        return;
      }
      this.branchSdk.logout();
      this.lastProcessedClickKey = null;
      this.lastIdentity = null; // allow developer_identity to be re-set on next login
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Logout failed');
    }
  }

  public async publishCustomEvent(eventType: string, params?: {}) {
    try {
      await this.initPromise;
      if (!this.branchSdk) {
        this.warnSdkUnavailable(`event '${eventType}'`);
        return;
      }
      LoggerProvider.info(this, 'Track Branch event: ' + eventType, params);
      const customData = {customData: params};
      this.branchSdk.sendBranchEvent(eventType, customData);
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Publish custom event failed', ex);
    }
  }

  async trackOrderEvents(order, customersProvider) {
    try {
      const analyticsData = this.orderProvider.getAnalyticsDataFromOrder(order);
      const transactionCount = order.countEnteredOrders;
      LoggerProvider.info(this, `Branch transaction count ${transactionCount}`);
      if (transactionCount === 1) { // in HTTP native we don't get an array when it's empty
        this.trackFirstOrder(analyticsData);
      }
      this.trackOrderCreation(analyticsData);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to create branch events', order);
    }
  }

}



// WEBPACK FOOTER //
// ./src/providers/analytics/branch.ts